import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { equals, reduce } from 'ramda'
import React from 'react'

import CustomTooltip from '@/app/component/atom/tooltip'
import { createConfirmation } from '@/app/contexts/confirmation.context'
import { cannot } from '@/app/helpers'
import { defaultRepeatQuestion } from '@/app/module/campaigns/definitions'
import { LanguageType } from '@/app/module/campaigns/helpers'
import {
  generateDefaultSpeechReplies,
  generateSpeechSettings,
  hasSpeechLanguage,
} from '@/app/module/campaigns/language-helpers'
import {
  CampaignTemplateType,
  Language,
  OnAnswerType,
  SenderIdOption,
  TabTypes,
  VoiceQuestion,
} from '@/app/module/campaigns/types'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

import { GetImageProps, GetVoiceProps } from './part-config-props'
import ResponseVoiceSpecific from './response-voice-specific'
import ResponseVoiceSpecificConfigs from './response-voice-specific-configs'
import { SpecificConfigs } from './response-voice-specific-configs-utils'
import ResponseVoiceSpeech from './response-voice-speech'
import ResponseVoiceSpeechConfigs from './response-voice-speech-configs'
import { SpeechConfigs } from './response-voice-speech-configs-utils'
import { SpeechValue } from './response-voice-speech-utils'
import ResponseVoiceSpoken from './response-voice-spoken'
import { SpokenValue } from './response-voice-spoken-utils'
import { defaultOnAnswerVoice, defaultSpokenResponse } from './types/multimessage/content'

type Props = {
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  files: FilesType
  id: string
  index: number
  item: VoiceQuestion
  languages: LanguageType[]
  orgId: number
  personalizationList: PersonalizationType
  senderIds: SenderIdOption[]
  timezones: TimezoneWithCountryCodeType
  timezone: string
  token: string
  type: CampaignTemplateType
  whatsappSenderIds: SenderIdOption[]

  changeHandler: (item: Partial<VoiceQuestion>) => void
  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
}

export default function CampaignContentQuestionVoice(props: Props) {
  const {
    item,
    id,
    getImageProps,
    getVoiceProps,
    personalizationList,
    senderIds,
    orgId,
    token,
    files,
    timezone,
    timezones,
    countryCode,
    countriesTimezones,
    languages,
    defaultLanguage,
    changeHandler,
    whatsappSenderIds,
  } = props
  const [responseType, setResponseType] = React.useState<TabTypes>(getResponseType(item))
  React.useEffect(() => {
    setResponseType(getResponseType(item))
  }, [item])

  const canEnableSpeech = React.useMemo(() => hasSpeechLanguage(languages), [languages])

  const handleChange = React.useCallback(
    (value: Partial<VoiceQuestion>) =>
      changeHandler({
        id: item.id,
        ...value,
      }),
    [item.id, changeHandler],
  )

  const speechLanguages = React.useMemo<Language[]>(
    () =>
      reduce(
        (acc: Language[], l: LanguageType) => {
          if (!l.speechLanguage) {
            return acc
          }

          return [
            ...acc,
            {
              name: l.speechLanguage.name,
              value: l.speechLanguage.value,
            },
          ]
        },
        [],
        languages,
      ),
    [languages],
  )

  const handleAnswerTypeChange = React.useCallback(
    async (_: React.SyntheticEvent, val: TabTypes) => {
      const currentResponseType = getResponseType(item)

      if (currentResponseType === val) {
        return
      }

      if (shouldConfirmSwitch(currentResponseType, item, languages, speechLanguages)) {
        const confirmed = await createConfirmation({
          title: 'Change response type',
          content:
            'Switching between response types will discard your current response config. Do you want to continue?',
        })

        if (!confirmed) {
          return
        }
      }

      return handleChange(generateContent(val, languages, speechLanguages))
    },
    [handleChange, item, languages, speechLanguages],
  )

  const actionProps = React.useMemo(
    () => ({
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      hasTranscribe: false,
      index: props.index,
      languages,
      personalizationList,
      senderIds,
      timezone,
      timezones,
      transcribeEnabled: false,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    }),
    [
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      languages,
      personalizationList,
      props.index,
      senderIds,
      timezone,
      timezones,
      whatsappSenderIds,
      getImageProps,
      getVoiceProps,
    ],
  )
  const apiProps = React.useMemo(() => ({ orgId, token }), [orgId, token])

  const handleVoiceSpecificChange = React.useCallback(
    (value: OnAnswerType[]) => handleChange({ onAnswer: value }),
    [handleChange],
  )

  const handleVoiceSpokenChange = React.useCallback(
    (spoken: SpokenValue) => changeHandler({ ...item, ...spoken }),
    [item, changeHandler],
  )

  const handleSpecificConfigsChange = React.useCallback(
    (configs: SpecificConfigs) => handleChange({ ...configs }),
    [handleChange],
  )
  const specificConfigs = React.useMemo(
    () => ({
      keypressAfterAudio: item.keypressAfterAudio,
      repeatQuestion: item.repeatQuestion || defaultRepeatQuestion,
      onInvalidReply: item.onInvalidReply,
      onTimeout: item.onTimeout,
      retries: item.retries,
      onRetriesExhausted: item.onRetriesExhausted,
    }),
    [
      item.keypressAfterAudio,
      item.repeatQuestion,
      item.onInvalidReply,
      item.onTimeout,
      item.retries,
      item.onRetriesExhausted,
    ],
  )
  const spoken = React.useMemo<SpokenValue | undefined>(() => {
    if (!item.spoken) {
      return undefined
    }
    return {
      processingReply: item.processingReply,
      spoken: item.spoken,
    }
  }, [item.processingReply, item.spoken])

  const handleSpeechConfigChange = React.useCallback(
    (configs: SpeechConfigs) => handleChange({ ...configs }),
    [handleChange],
  )
  const speechConfigs = React.useMemo(
    () => ({
      onTimeout: item.onTimeout,
      retries: item.retries,
      onRetriesExhausted: item.onRetriesExhausted,
      speechSettings: item.speechSettings,
    }),
    [item.onTimeout, item.retries, item.onRetriesExhausted, item.speechSettings],
  )
  const handleSpeechChange = React.useCallback((speech: SpeechValue) => handleChange({ ...speech }), [handleChange])
  const speech = React.useMemo<SpeechValue | undefined>(() => {
    if (!item.speechSettings) {
      return undefined
    }
    return {
      onAnswer: item.onAnswer,
      processingReply: item.processingReply,
      speechSettings: item.speechSettings,
      useAi: item.useAi,
    }
  }, [item.onAnswer, item.processingReply, item.speechSettings, item.useAi])

  return (
    <div
      className="voice-q-choice-block"
      style={{
        marginTop: '2rem',
      }}
    >
      <Divider
        style={{
          margin: '1rem -24px',
        }}
      />
      <Tabs value={responseType} indicatorColor="primary" onChange={handleAnswerTypeChange}>
        <Tab
          value="keypress"
          label="Keypress Response"
          style={{ zIndex: 2 }}
          icon={<Icon>dialpad</Icon>}
          wrapped
          className="campaign-keypress-tab"
        />
        {canEnableSpeech && (
          <Tab
            value="speech"
            label="Speech Recognition"
            style={{ zIndex: 2 }}
            icon={<Icon>mic</Icon>}
            wrapped
            className="campaign-speech-tab"
          />
        )}
        {!canEnableSpeech && (
          <CustomTooltip title="You have to setup a speech language before you can start using speech recognition">
            <div>
              <Tab
                disabled
                value="speech"
                label="Speech Recognition"
                style={{ zIndex: 2 }}
                icon={<Icon>mic</Icon>}
                wrapped
                className="campaign-speech-tab"
              />
            </div>
          </CustomTooltip>
        )}
        <Tab
          value="spoken"
          label="Spoken Response"
          style={{ zIndex: 2 }}
          icon={<Icon>voicemail</Icon>}
          wrapped
          className="campaign-spoken-tab"
        />
      </Tabs>
      <Divider
        style={{
          margin: '-1px -24px 1rem',
        }}
      />
      {responseType === 'keypress' && (
        <div className="campaign-keypress-tab-content">
          <ResponseVoiceSpecific
            actionProps={actionProps}
            apiProps={apiProps}
            campaignType={props.type}
            id={props.id}
            onAnswer={item.onAnswer}
            onChange={handleVoiceSpecificChange}
          />
          <ResponseVoiceSpecificConfigs
            actionProps={actionProps}
            apiProps={apiProps}
            campaignType={props.type}
            configs={specificConfigs}
            id={id}
            onChange={handleSpecificConfigsChange}
          />
        </div>
      )}
      {responseType === 'speech' && !!speech && (
        <div className="campaign-speech-tab-content">
          <ResponseVoiceSpeech
            actionProps={actionProps}
            apiProps={apiProps}
            campaignType={props.type}
            id={props.id}
            speech={speech}
            onChange={handleSpeechChange}
          />
          {!!speechConfigs.speechSettings && (
            <ResponseVoiceSpeechConfigs
              actionProps={actionProps}
              apiProps={apiProps}
              campaignType={props.type}
              configs={speechConfigs as SpeechConfigs}
              id={id}
              useAi={speech.useAi}
              onChange={handleSpeechConfigChange}
            />
          )}
        </div>
      )}
      {responseType === 'spoken' && !!spoken && (
        <ResponseVoiceSpoken
          actionProps={actionProps}
          apiProps={apiProps}
          campaignType={props.type}
          id={props.id}
          spoken={spoken}
          onChange={handleVoiceSpokenChange}
        />
      )}
    </div>
  )
}

const getResponseType = (item: VoiceQuestion) => {
  if (item.spoken) {
    return 'spoken'
  }
  if (item.speechSettings) {
    return 'speech'
  }
  return 'keypress'
}

const getSpeechOnAnswer = (speechLanguages: Language[]) => [
  {
    speechReplies: generateDefaultSpeechReplies(speechLanguages),
    ranges: [],
    actions: [],
    label: '',
  },
  {
    speechReplies: generateDefaultSpeechReplies(speechLanguages),
    ranges: [],
    actions: [],
    label: '',
  },
]

const generateContent = (
  responseType: TabTypes,
  languages: LanguageType[],
  speechLanguages: Language[],
): Partial<VoiceQuestion> => {
  switch (responseType) {
    case 'keypress':
      return {
        spoken: undefined,
        onAnswer: defaultOnAnswerVoice,
        speechSettings: undefined,
        useAi: false,
      }
    case 'spoken':
      return {
        onAnswer: [],
        spoken: defaultSpokenResponse,
        speechSettings: undefined,
        useAi: false,
      }
    case 'speech':
      return {
        spoken: undefined,
        onAnswer: getSpeechOnAnswer(speechLanguages),
        speechSettings: generateSpeechSettings(languages),
        useAi: false,
      }
    default:
      return cannot(responseType)
  }
}

export const shouldConfirmSwitch = (
  responseType: TabTypes,
  item: VoiceQuestion,
  languages: LanguageType[],
  speechLanguages: Language[],
) => {
  switch (responseType) {
    case 'keypress':
      return !equals(item.onAnswer, defaultOnAnswerVoice)
    case 'spoken':
      return !equals(item.spoken, defaultSpokenResponse)
    case 'speech': {
      const defaultSpeechState = generateContent('speech', languages, speechLanguages)

      return (
        !equals(item.onAnswer, defaultSpeechState.onAnswer) ||
        !equals(item.speechSettings, defaultSpeechState.speechSettings) ||
        item.useAi !== defaultSpeechState.useAi
      )
    }
    default:
      return cannot(responseType)
  }
}
